import {
  configureStore,
  combineReducers,
  Reducer,
  ActionFromReducersMapObject,
  StateFromReducersMapObject,
} from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { useDispatch } from 'react-redux'
import { crsApi } from './api/crs/react'
import { ccsApi } from './api/ccs/react'
import { api as gcsApi } from './api/gcs/generated'
import metadataReducer, { name as metadataSliceName } from './slices/metadata'
import genericModuleConfigurationReducer, {
  name as genericModuleConfigurationSliceName,
} from './slices/genericModuleConfiguration'
import moduleHistoryReducer, { name as moduleHistorySliceName } from './slices/moduleHistory'
import notificationsReducer, { name as notificationsSliceName } from './slices/notifications'
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist'
import { root, metadata } from './persist.config'
import crsAuthMiddleware from './middleware/crsAuth'

const reducers = {
  [metadataSliceName]: persistReducer(metadata, metadataReducer),
  [notificationsSliceName]: notificationsReducer,
  [genericModuleConfigurationSliceName]: genericModuleConfigurationReducer,
  [crsApi.reducerPath]: crsApi.reducer,
  [gcsApi.reducerPath]: gcsApi.reducer,
  [ccsApi.reducerPath]: ccsApi.reducer,
  [moduleHistorySliceName]: moduleHistoryReducer,
}
type ReducersMapObject = typeof reducers
// See below comment/issue as to why we need to explicitely type `rootReducer`
// - https://github.com/reduxjs/redux-toolkit/issues/2068#issuecomment-1130796500
export const rootReducer: Reducer<
  StateFromReducersMapObject<ReducersMapObject>,
  ActionFromReducersMapObject<ReducersMapObject>
> = combineReducers(reducers)
const persistedReducer = persistReducer(root, rootReducer)

const createStore = () => {
  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(crsApi.middleware, gcsApi.middleware, ccsApi.middleware, crsAuthMiddleware),
  })
  setupListeners(store.dispatch)
  const persistor = persistStore(store)
  return { store, persistor }
}

export type AppStore = ReturnType<typeof createStore>['store']
export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = AppStore['dispatch']
// react-redux 8's plain `useDispatch()` types as `Dispatch<AnyAction>`, which rejects
// RTK's `AsyncThunkAction` returns from `dispatch(someAsyncThunk())`; this typed variant
// carries the store's actual (thunk-aware) dispatch type.
export const useAppDispatch: () => AppDispatch = useDispatch

export default createStore
